import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff } from 'lucide-react';
import { cx } from '../../lib/cx';
import {
  fetchWatchers,
  unwatchRequest,
  watchKeys,
  watchRequest,
  type WatchReason,
} from '../notifications/api';

/**
 * Follow this request, or stop.
 *
 * One control for one person: whether *you* hear about this request. It is not
 * a followers panel — an earlier version was, sitting in the left column with
 * a list of everyone and a picker for adding others, and it was wrong twice
 * over. It gave a whole section to a one-bit setting, and its picker overflowed
 * that column the moment it opened.
 *
 * It SAYS "Follow" and "Following" rather than standing on a bell alone. A
 * lone bell has to be decoded — is it a notification count, a mute, something
 * that will start emailing me? — and the answer arrived only through a native
 * `title`, which needs a pointer, a pause, and the patience to wait for it.
 * The word costs about forty pixels on a strip that has them spare, and
 * removes the guess. The icon stays beside it, doing what an icon is good at:
 * telling the two states apart at a glance.
 *
 * Adding somebody else is not done here at all, because it is not a separate
 * act: @-mentioning someone in the thread already subscribes them (see
 * NotificationsService.notifyMentions), which is the same gesture people
 * already use to get someone's attention. A second, hidden way to do it is a
 * second place to look.
 *
 * WHO is following still arrives with the state and stays in the tooltip —
 * that list is the one thing the old panel showed which a control this size
 * cannot, and it costs nothing to keep for whoever hovers.
 */

const REASON_LABEL: Readonly<Record<WatchReason, string>> = {
  creator: 'raised it',
  commenter: 'replied',
  mentioned: 'was mentioned',
  opted_in: 'follows it',
  added: 'was added',
};

export interface FollowToggleProps {
  readonly workspaceId: string;
  readonly requestId: string;
  readonly className?: string;
}

export function FollowToggle({ workspaceId, requestId, className }: FollowToggleProps) {
  const queryClient = useQueryClient();
  const key = watchKeys.forRequest(workspaceId, requestId);

  const query = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => fetchWatchers(workspaceId, requestId, signal),
  });

  const watching = query.data?.watching ?? false;
  const items = query.data?.items ?? [];

  const toggle = useMutation({
    // Typed as the shared shape: the two calls return `{watching: true}` and
    // `{watching: false}` as literal types, which have no common supertype
    // TypeScript will infer across a conditional.
    mutationFn: async (): Promise<{ watching: boolean }> =>
      watching ? unwatchRequest(workspaceId, requestId) : watchRequest(workspaceId, requestId),
    // Flip immediately and put it back if the server disagrees. The whole
    // control is one bit, so a spinner on it would be longer-lived than the
    // thing it describes.
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (current: typeof query.data) =>
        current === undefined ? current : { ...current, watching: !current.watching },
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const others = items.filter((item) => !(watching && item.reason === 'opted_in'));
  // Names, for whoever hovers. The label already says what the button does, so
  // this is purely the extra: who else is on this thread and why.
  const roster =
    others.length > 0 ? others.map((item) => `${item.name} ${REASON_LABEL[item.reason]}`).join(', ') : null;
  const title = watching
    ? `You are following this${roster === null ? '' : `, with ${roster}`}. Click to stop.`
    : `Get notified about this request${roster === null ? '' : ` — ${roster}`}`;

  return (
    <button
      type="button"
      title={title}
      aria-pressed={watching}
      onClick={() => toggle.mutate()}
      className={cx(
        'flex items-center gap-1.5 rounded-md px-2 py-1 fs-11.5 font-semibold hover:bg-surface-2',
        watching ? 'text-accent' : 'text-muted hover:text-ink',
        className,
      )}
    >
      {watching ? <Bell size={13} strokeWidth={2} /> : <BellOff size={13} strokeWidth={2} />}
      {watching ? 'Following' : 'Follow'}
      {/* How many people are on the thread, not a count of anything unread.
          Muted and separated, so it reads as a detail of the label rather than
          part of it. */}
      {items.length > 0 && <span className="font-normal text-faint numeric">· {items.length}</span>}
    </button>
  );
}
